import EmailIcon from '@mui/icons-material/Email';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import SmsIcon from '@mui/icons-material/Sms';
import {
  Box,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import React from 'react';
import { useParams } from 'react-router-dom';

import { ChipStatus } from '@/components/AppShell/Drawer';
import { getChipStatusProps } from '@/components/StatusIcon/statusMapping';
import { Permission, useUserPermissions } from '@/hooks/useUserPermissions';
import { useStructure } from '@/Structures/useStructures';
import { naturalCompare } from '@/util';

import { getAlertHistory } from '../api';
import type {
  AlertDetailsType,
  AlertHistoryRecord,
  AlertHistoryResponse,
  AlertNotification,
} from '../types';

const ALERT_TYPE_DEVICE_STATUS: Partial<Record<AlertDetailsType, string>> = {
  LEAK: 'ERROR_LEAK',
  HIGH_USAGE_ALERT: 'ERROR_LEAK',
  MESSAGES_SEEN: 'OKAY',
};

const ALERT_TYPE_LABELS: Record<AlertDetailsType, string> = {
  NO_DETAILS: 'Alert',
  MESSAGES_SEEN: 'Device Connected',
  METER_MODE_ACHIEVED: 'Meter Mode Achieved',
  INITIAL_METER_HEALTHY: 'Initial Health Confirmed',
  METER_UNHEALTHY: 'Meter Unhealthy',
  METER_HEALTHY: 'Meter Health Restored',
  LEAK: 'Leak Detected',
  HIGH_USAGE_ALERT: 'High Flow Alert',
};

const formatAlertDescription = (record: AlertHistoryRecord): string => {
  const details = record.alert_details;
  if (details === null) return '';

  if (record.alert_details_type === 'LEAK') {
    const duration = details.leak_duration_hours;
    const usage = details.leak_total_usage_gallons;
    const rate = details.leak_average_flow_rate_gpm;
    return `${duration?.toFixed(1)}h duration, ${usage?.toFixed(1)} gal total, ${rate?.toFixed(
      2,
    )} GPM avg`;
  }

  if (record.alert_details_type === 'HIGH_USAGE_ALERT') {
    const usage = details.alert_usage_gallons;
    const window = details.current_usage_alert_window_hours;
    const threshold = details.current_usage_alert_threshold_gallons;
    return `${usage?.toFixed(1)} gal in ${window}h window (threshold: ${threshold} gal)`;
  }

  return '';
};

const formatTimestamp = (epochSeconds: number, timeZone?: string): string => {
  const d = new Date(epochSeconds * 1000);
  const tz = timeZone || undefined;
  const datePart = d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: tz,
  });
  const timePart = d.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: tz,
    timeZoneName: 'short',
  });
  return `${datePart}\n${timePart}`;
};

interface GroupedNotification {
  channel: string;
  type: string;
  delivery_status: string;
  count: number;
  contacts: string[];
}

const groupNotifications = (notifications: AlertNotification[]): GroupedNotification[] => {
  const groups: Record<string, GroupedNotification> = {};
  for (const n of notifications) {
    const key = `${n.channel}-${n.type}-${n.delivery_status}`;
    if (groups[key]) {
      groups[key].count += 1;
      if (n.contact) groups[key].contacts.push(n.contact);
    } else {
      groups[key] = {
        channel: n.channel,
        type: n.type,
        delivery_status: n.delivery_status,
        count: 1,
        contacts: n.contact ? [n.contact] : [],
      };
    }
  }
  return Object.values(groups);
};

const renderNotificationChips = (
  notifications: AlertNotification[],
  type: 'USER' | 'SUBSCRIBER',
) => {
  const filtered = notifications.filter((n) => n.type === type);
  if (filtered.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        —
      </Typography>
    );
  }

  const grouped = groupNotifications(filtered);
  const statusOrder: Record<string, number> = { SENT: 0, FAILED: 1, PENDING: 2 };
  const sortByStatus = (a: GroupedNotification, b: GroupedNotification) =>
    (statusOrder[a.delivery_status] ?? 3) - (statusOrder[b.delivery_status] ?? 3);

  const emailChips = grouped.filter((g) => g.channel === 'EMAIL').sort(sortByStatus);
  const smsChips = grouped.filter((g) => g.channel === 'SMS').sort(sortByStatus);

  const renderRow = (icon: React.ReactElement, chips: GroupedNotification[]) => {
    if (chips.length === 0) return null;
    return (
      <Stack direction="row" gap={0.5} alignItems="center">
        {icon}
        {chips.map((g, i) => {
          let color: 'success' | 'error' | 'default' = 'default';
          if (g.delivery_status === 'SENT') color = 'success';
          else if (g.delivery_status === 'FAILED') color = 'error';

          const label = g.count > 1 ? String(g.count) : '1';

          const tooltipContent =
            g.contacts.length > 0
              ? `${g.delivery_status}\n${g.contacts.join('\n')}`
              : `${g.delivery_status}`;

          return (
            <Tooltip
              key={i}
              title={<span style={{ whiteSpace: 'pre-line' }}>{tooltipContent}</span>}
            >
              <Chip label={label} size="small" color={color} variant="outlined" />
            </Tooltip>
          );
        })}
      </Stack>
    );
  };

  return (
    <Stack direction="column" gap={0.5}>
      {renderRow(<EmailIcon fontSize="small" color="action" />, emailChips)}
      {renderRow(<SmsIcon fontSize="small" color="action" />, smsChips)}
    </Stack>
  );
};

const AlertsTable = () => {
  const { structureId } = useParams<{ structureId: string }>();
  const { structure } = useStructure(structureId!);
  // Alert history is gated behind VIEW_USAGE on the backend; billing managers lack it.
  // Don't fire the request when we already know it will be denied (avoids an error toast).
  const { hasPermission, loading: permissionsLoading } = useUserPermissions();
  const hasViewUsage = hasPermission(Permission.VIEW_USAGE);
  // Optimistic flag for the UI only, so we don't flash the "no permission" message while
  // permissions are still loading. The fetch itself waits for permissions to resolve.
  const canViewAlertHistory = permissionsLoading || hasViewUsage;
  const [data, setData] = React.useState<AlertHistoryResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(25);
  const [unitFilter, setUnitFilter] = React.useState<string>('');

  const units = React.useMemo(() => structure?.units ?? [], [structure?.units]);

  const unitNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const unit of units) {
      map[unit.id] = unit.name;
    }
    return map;
  }, [units]);

  const deviceNameMap = React.useMemo(() => {
    const map: Record<string, string> = {};
    for (const unit of units) {
      for (const device of unit.devices ?? []) {
        map[device.id] = device.name || device.serial_number;
      }
    }
    return map;
  }, [units]);

  React.useEffect(() => {
    if (!structureId) return;
    // Wait until permissions resolve before deciding. Gating on the optimistic flag would
    // fire the request during the loading window and surface an error toast before we know
    // the user lacks access.
    if (permissionsLoading) return;
    if (!hasViewUsage) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    getAlertHistory(structureId, page + 1, rowsPerPage, unitFilter || undefined).then((resp) => {
      if (!cancelled) {
        setData(resp);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [structureId, page, rowsPerPage, unitFilter, permissionsLoading, hasViewUsage]);

  if (!canViewAlertHistory) {
    return (
      <Paper
        variant="outlined"
        sx={{
          borderColor: 'divider',
          borderWidth: 2,
          borderRadius: 2,
          mt: -2,
        }}
      >
        <Box mx={3} my={2}>
          <Typography variant="h5" component="div" fontWeight={600}>
            Alert History
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 1 }}>
            You don't have permission to view alert history. Please contact your account owner.
          </Typography>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      variant="outlined"
      sx={{
        borderColor: 'divider',
        borderWidth: 2,
        borderRadius: 2,
        mt: -2,
      }}
    >
      <Box mx={3} my={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5" component="div" fontWeight={600}>
            Alert History
          </Typography>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <Select
              value={unitFilter}
              displayEmpty
              renderValue={(value) => {
                if (!value) return 'All Units';
                const unitName = unitNameMap[value];
                return unitName ?? value;
              }}
              onChange={(e) => {
                setUnitFilter(e.target.value);
                setPage(0);
              }}
            >
              <MenuItem value="">All Units</MenuItem>
              {[...units]
                .sort((a, b) => naturalCompare(a.name, b.name))
                .map((unit) => (
                  <MenuItem key={unit.id} value={unit.id}>
                    {unit.name}
                  </MenuItem>
                ))}
            </Select>
          </FormControl>
        </Stack>
      </Box>
      <Divider sx={{ borderColor: 'grey', width: '100%' }} />
      <TableContainer>
        <Table size="medium" sx={{ minWidth: 650 }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 200 }}>Date/Time</TableCell>
              <TableCell sx={{ width: 120 }}>Unit</TableCell>
              <TableCell sx={{ width: 100 }}>Device</TableCell>
              <TableCell sx={{ width: 180 }}>Event</TableCell>
              <TableCell sx={{ width: 140 }}>Users</TableCell>
              <TableCell sx={{ width: 140 }}>Tenants</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : (data?.alerts ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ py: 10 }} align="center">
                  <Box display="flex" flexDirection="column" alignItems="center" gap={3}>
                    <Typography color="text.secondary">No alert history found.</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data!.alerts.map((row) => (
                <TableRow key={row.id} hover>
                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {formatTimestamp(row.created, structure?.timezone_key)}
                  </TableCell>
                  <TableCell>{unitNameMap[row.unit_id] ?? row.unit_id}</TableCell>
                  <TableCell>{deviceNameMap[row.device_id] ?? row.device_id}</TableCell>
                  <TableCell>
                    <Stack direction="row" gap={1} alignItems="center">
                      {(() => {
                        const deviceStatus = ALERT_TYPE_DEVICE_STATUS[row.alert_details_type];
                        if (deviceStatus) {
                          const props = getChipStatusProps(deviceStatus);
                          if (props) return <ChipStatus {...props} text="" />;
                        }
                        return null;
                      })()}
                      {ALERT_TYPE_LABELS[row.alert_details_type] ?? row.alert_details_type}
                      {formatAlertDescription(row) && (
                        <Tooltip title={formatAlertDescription(row)}>
                          <InfoOutlinedIcon sx={{ fontSize: '17px', color: '#555' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>{renderNotificationChips(row.notifications, 'USER')}</TableCell>
                  <TableCell>{renderNotificationChips(row.notifications, 'SUBSCRIBER')}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[10, 25, 50]}
        count={data?.total ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={(_e, newPage) => setPage(newPage)}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(parseInt(e.target.value, 10));
          setPage(0);
        }}
        component="div"
      />
    </Paper>
  );
};

export default AlertsTable;
